import api from './api';
import type {
  MaterialReceiptRow, MaterialReceiptPatch, MaterialReceiptCreate, MatchJobInput,
  ThreadMessage, ThreadMessageCreate, ThreadResponse,
} from '../types/api';
import { materialTypeFromCategory } from '../utils/jobCategory';

// The backend doesn't send materialType yet — it's a frontend-derived,
// first-class field computed from job_category at the API boundary so every
// MaterialReceiptRow consistently carries it.
function tag(row: MaterialReceiptRow): MaterialReceiptRow {
  return { ...row, materialType: materialTypeFromCategory(row.job_category) };
}

export async function getMaterialReceipts(): Promise<MaterialReceiptRow[]> {
  const r = await api.get<MaterialReceiptRow[]>('/api/v1/material-receipts');
  return r.data.map(tag);
}

export async function getMaterialReceipt(id: string): Promise<MaterialReceiptRow> {
  const r = await api.get<MaterialReceiptRow>(`/api/v1/material-receipts/${id}`);
  return tag(r.data);
}

export async function createMaterialReceipt(payload: MaterialReceiptCreate): Promise<MaterialReceiptRow> {
  const r = await api.post<MaterialReceiptRow>('/api/v1/material-receipts', payload);
  return tag(r.data);
}

// Confirm a manual job match: links each job (with its status/type/NAA). By
// default this flips the receipt to Matched; pass setMatched=false to persist
// the job edits while leaving the receipt's status untouched (drawer "Save").
export async function confirmReceiptMatch(
  id: string,
  jobs: MatchJobInput[],
  setMatched = true,
): Promise<MaterialReceiptRow> {
  const r = await api.post<MaterialReceiptRow>(`/api/v1/material-receipts/${id}/match`, { jobs, set_matched: setMatched });
  return tag(r.data);
}

export async function updateMaterialReceipt(
  id: string,
  patch: MaterialReceiptPatch,
): Promise<MaterialReceiptRow> {
  const r = await api.patch<MaterialReceiptRow>(`/api/v1/material-receipts/${id}`, patch);
  return tag(r.data);
}

// Merge an arriving reorder receipt (source) into the original Received-Short
// ticket (target): sums received quantities, links + soft-deletes the source,
// and returns the target to To Verify.
export async function mergeReorderReceipt(
  targetId: string,
  sourceReceiptId: string,
): Promise<MaterialReceiptRow> {
  const r = await api.post<MaterialReceiptRow>(
    `/api/v1/material-receipts/${targetId}/merge`,
    { source_receipt_id: sourceReceiptId },
  );
  return tag(r.data);
}

export async function deleteMaterialReceipt(id: string): Promise<void> {
  await api.delete(`/api/v1/material-receipts/${id}`);
}

// ── Receipt thread — the shared, persistent memory log of one receipt ───────

export async function getReceiptThread(receiptId: string): Promise<ThreadResponse> {
  const r = await api.get<ThreadResponse>(`/api/v1/material-receipts/${receiptId}/thread`);
  return r.data;
}

export async function postReceiptThreadMessage(
  receiptId: string,
  msg: ThreadMessageCreate,
): Promise<ThreadMessage> {
  const r = await api.post<ThreadMessage>(`/api/v1/material-receipts/${receiptId}/thread/messages`, msg);
  return r.data;
}

// Local chat message → thread payload: keep everything serializable, drop the
// function handlers off quick-actions (labels survive; same fidelity a local
// reload has today).
export function toThreadPayload(msg: Record<string, unknown>): ThreadMessageCreate {
  const { id, role, content, ts, ...rest } = msg as any;
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (typeof value === 'function') continue;
    if (key === 'actions' && Array.isArray(value)) {
      extras.actions = value.map((a: any) => {
        const { fn, ...keep } = a || {};
        return keep;
      });
    } else {
      extras[key] = value;
    }
  }
  return {
    role: role === 'user' ? 'user' : 'agent',
    content: String(content ?? ''),
    extras: Object.keys(extras).length ? extras : null,
  };
}
